import { BaseComponent } from '../base-component'
import { $el, transitionToPromise } from '../../utils/utils'
import { skipTransition } from '../../utils/dom'
import { BodyElement } from '../body-element/body-element'

const CONTENT_WRAP_SELECTOR = `[data-accordeon-content-wrap]`
const CONTENT_SELECTOR = `[data-accordeon-content]`
const BODY_ELEMENT_SELECTOR = 'body-element'

export class AccordeonComponent extends BaseComponent {
  resizeObserver: ResizeObserver
  isExpanded: boolean

  mountComponent(): void {
    const content = $el(CONTENT_SELECTOR, this)
    this.isExpanded = this.getAttribute('data-aria-expanded') === 'true'

    const isInCartDrawer =
      this.closest('.cart-drawer') !== null ||
      this.classList.contains('cart-drawer-subtotal__sale-label')

    if (isInCartDrawer && window.innerWidth <= 749) {
      this.isExpanded = false
      this.setAttribute('data-aria-expanded', 'false')
    }

    this.updateHeight()

    this.addListener(this, 'focusout', this.handleAccordeonFocusout)
    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(content)

    if (this.isEditor) {
      this.editor.on('BLOCK_SELECT', this.handleBlockSelect)
      this.editor.on('BLOCK_DESELECT', this.handleBlockDeselect)
    }
  }

  unmountComponent(): void {
    this.resizeObserver.disconnect()
  }

  handleAccordeonFocusout = () => {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

    if (!this.isExpanded && bodyElement.isUsingKeyboard) {
      this.show()
    }
  }

  handleResize = (): void => {
    this.updateHeight()
  }

  handleBlockSelect = ({ detail: { sectionId, blockId, load } }): void => {
    if (
      this.dataset.sectionId === sectionId &&
      this.dataset.blockId === blockId &&
      !this.isExpanded
    ) {
      const content = $el(CONTENT_SELECTOR, this)

      skipTransition(content, load)

      this.show().then(() => {
        skipTransition(content, false)
      })
    }
  }

  handleBlockDeselect = ({ detail: { sectionId, blockId } }): void => {
    if (
      this.dataset.sectionId === sectionId &&
      this.dataset.blockId === blockId &&
      this.isExpanded
    ) {
      this.hide()
    }
  }

  async hide(): Promise<void> {
    await this.setExpand(false)
    this.emit('hide', { blockId: this.dataset.blockId })
  }

  async show(): Promise<void> {
    await this.setExpand(true)
    this.emit('show', { blockId: this.dataset.blockId })
  }

  async toggle(): Promise<void> {
    await this.setExpand(!this.isExpanded)
    this.emit('toggle', { blockId: this.dataset.blockId })
  }

  updateHeight(): void {
    const contentWrap = $el(CONTENT_WRAP_SELECTOR, this)
    const content = $el(CONTENT_SELECTOR, this)

    if (content) {
      contentWrap.style.maxHeight = `${Math.ceil(
        this.isExpanded ? content.scrollHeight : 0,
      )}px`
    }
  }

  async setExpand(isExpanded: boolean): Promise<void> {
    this.isExpanded = isExpanded
    this.setAttribute('data-aria-expanded', isExpanded ? 'true' : 'false')
    this.updateHeight()
    await transitionToPromise(this)
  }
}
